/**
 * P-1 日報一覧 (§14-2)。自分/自店の日報を探す・「今日の日報を書く」への入口。
 * 増分1では最小限の実装 (ステータスバッジ付き一覧のみ)。V-2/V-3 の作り込みは増分2の対象。
 * バックエンド: apps/api/src/routes/daily-reports.ts GET /daily-reports。
 * 設計: docs/design/v05-daily-report-detail.md §14-2
 *
 * ⚠️ §14-2 冒頭ルール: カラートークンは docs/ui-guidelines.md §2.1 のもの (success/alert/danger/
 * surface-sunken 等) のみを使う。生の Tailwind 色クラス (bg-amber-50 等、RequestsPage.tsx の
 * 既存パターン) はここでは踏襲しない。
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, Loader2 } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { Button } from '@/components/Button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useScopeStore, findNodeInTree, type NodesResponse } from '@/stores/scope';
import { flattenNodes } from '@/lib/node-tree';
import { fetchDailyReports, type DailyReportListRow, type DailyReportStatus } from '@/lib/daily-report-api';

const STATUS_LABEL: Record<DailyReportStatus, string> = {
  draft: '下書き', submitted: '提出済み', approved: '承認済み', rejected: '差し戻し',
};
const STATUS_CLS: Record<DailyReportStatus, string> = {
  draft: 'bg-surface-sunken text-text-sub',
  submitted: 'bg-success-bg text-success-text',
  approved: 'bg-success-bg text-success-text',
  rejected: 'bg-danger-bg text-danger-text',
};
const RECON_LABEL: Record<string, string> = {
  not_evaluated: '未評価', not_applicable: '対象外', ok: '一致', ng: '要確認', ng_accepted: '受入済み',
};
const RECON_CLS: Record<string, string> = {
  not_evaluated: 'bg-surface-sunken text-text-sub',
  not_applicable: 'bg-surface-sunken text-text-sub',
  ok: 'bg-success-bg text-success-text',
  ng: 'bg-alert-bg text-alert-text',
  ng_accepted: 'bg-surface-sunken text-text-sub',
};

const StatusBadge = ({ status }: { status: string }) => (
  <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium whitespace-nowrap ${STATUS_CLS[status as DailyReportStatus] ?? 'bg-surface-sunken text-text-sub'}`}>
    {STATUS_LABEL[status as DailyReportStatus] ?? status}
  </span>
);
const ReconciliationBadge = ({ status }: { status: string }) => (
  <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium whitespace-nowrap ${RECON_CLS[status] ?? 'bg-surface-sunken text-text-sub'}`}>
    {RECON_LABEL[status] ?? status}
  </span>
);

const localDateString = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const todayLocalDateString = () => localDateString(new Date());
const daysAgoLocalDateString = (days: number) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return localDateString(d);
};

const ALL_NODES = '__all__';
const ALL_STATUSES = '__all__';

export function DailyReportsListPage() {
  const navigate = useNavigate();
  const scopeRoots = useScopeStore((s) => s.roots);
  const globalSelectedNodeId = useScopeStore((s) => s.selectedNodeId);
  // ⚠️ NodeSwitcher (AppLayout) が既に GET /nodes を呼び useScopeStore に流し込んでいるはずだが、
  // このページへ直接遷移された場合 (ブックマーク等) にも備え、自前でも取得する (KpiTargetsPage.tsx と同型)。
  const { data: nodesData } = useQuery<NodesResponse>({ queryKey: ['nodes'], queryFn: () => apiFetch<NodesResponse>('/nodes') });
  const roots = nodesData?.roots ?? scopeRoots;
  const flatNodes = useMemo(() => flattenNodes(roots), [roots]);
  const nodeLabelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const { node } of flatNodes) map.set(node.node_id, node.name);
    return map;
  }, [flatNodes]);

  const [nodeFilter, setNodeFilter] = useState<string>(ALL_NODES);
  const [statusFilter, setStatusFilter] = useState<string>(ALL_STATUSES);
  const [from, setFrom] = useState(daysAgoLocalDateString(30));
  const [to, setTo] = useState(todayLocalDateString());

  const { data: rows = [], isLoading, isError } = useQuery<DailyReportListRow[]>({
    queryKey: ['daily-reports', nodeFilter, statusFilter, from, to],
    queryFn: () => fetchDailyReports({
      node_id: nodeFilter === ALL_NODES ? undefined : nodeFilter,
      status: statusFilter === ALL_STATUSES ? undefined : (statusFilter as DailyReportStatus),
      from: from || undefined,
      to: to || undefined,
    }),
  });

  // 「今日の日報を書く」の対象店舗: 一覧の店舗フィルタが単一店舗に絞られていればそれを使い、
  // 「すべての店舗」表示中はグローバルスコープ (NodeSwitcher の現在選択) にフォールバックする。
  // どちらも無ければ (全社表示中かつ未絞り込み) ボタンを無効化し、店舗選択を促す。
  const openTargetNodeId = nodeFilter !== ALL_NODES ? nodeFilter : globalSelectedNodeId;

  const findNodeName = (nodeId: string) => nodeLabelById.get(nodeId) ?? findNodeInTree(roots, nodeId)?.name ?? nodeId;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-h1">日報</h1>
          <p className="text-sm text-text-sub mt-0.5">自分・自店の日報を探します。</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Button
            variant="primary" leftIcon={<Plus size={16} />}
            disabled={!openTargetNodeId}
            onClick={() => openTargetNodeId && navigate(`/daily-reports/open?node_id=${openTargetNodeId}`)}
          >
            今日の日報を書く
          </Button>
          {!openTargetNodeId && (
            <p className="text-xs text-text-sub">店舗を選択すると日報を書けます</p>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Select value={nodeFilter} onValueChange={setNodeFilter}>
          <SelectTrigger className="w-full sm:w-56 min-h-[44px]" aria-label="店舗で絞り込み"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_NODES}>すべての店舗</SelectItem>
            {flatNodes.map(({ node, depth }) => (
              <SelectItem key={node.node_id} value={node.node_id}>{'　'.repeat(depth)}{node.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-40 min-h-[44px]" aria-label="状態で絞り込み"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_STATUSES}>すべての状態</SelectItem>
            {Object.entries(STATUS_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1.5">
          <input type="date" aria-label="開始日" value={from} max={to} onChange={(e) => setFrom(e.target.value)}
            className="min-h-[44px] px-2 py-2 rounded border border-surface-border text-sm" />
          <span className="text-text-sub text-sm">〜</span>
          <input type="date" aria-label="終了日" value={to} min={from} max={todayLocalDateString()} onChange={(e) => setTo(e.target.value)}
            className="min-h-[44px] px-2 py-2 rounded border border-surface-border text-sm" />
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-text-sub py-8">
          <Loader2 className="w-5 h-5 animate-spin" aria-hidden />
          <span className="text-body">読み込んでいます…</span>
        </div>
      ) : isError ? (
        <p className="text-sm text-danger-text">日報一覧の取得に失敗しました。</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-text-sub">該当する日報はありません。</p>
      ) : (
        <>
          {/* モバイル: カード */}
          <ul className="space-y-2 md:hidden">
            {rows.map((r) => (
              <li key={r.id}>
                <button type="button" onClick={() => navigate(`/daily-reports/${r.id}`)}
                  className="w-full text-left border border-surface-border rounded-lg bg-white p-3 min-h-[44px]">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-text-body">{r.business_date}</span>
                    <StatusBadge status={r.status} />
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-1">
                    <span className="text-xs text-text-sub break-words">{findNodeName(r.node_id)}</span>
                    <ReconciliationBadge status={r.reconciliation_status} />
                  </div>
                </button>
              </li>
            ))}
          </ul>

          {/* デスクトップ: テーブル */}
          <div className="hidden md:block overflow-x-auto border border-surface-border rounded-lg bg-white">
            <table className="w-full text-sm">
              <thead className="bg-surface-sunken">
                <tr>
                  <th scope="col" className="text-left px-3 py-2 font-medium text-text-sub">営業日</th>
                  <th scope="col" className="text-left px-3 py-2 font-medium text-text-sub">店舗</th>
                  <th scope="col" className="text-left px-3 py-2 font-medium text-text-sub">状態</th>
                  <th scope="col" className="text-left px-3 py-2 font-medium text-text-sub">突合</th>
                  <th scope="col" className="text-left px-3 py-2 font-medium text-text-sub">提出日時</th>
                  <th scope="col" className="text-right px-3 py-2 font-medium text-text-sub">操作</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-surface-border">
                    <td className="px-3 py-2 text-text-body tabular-nums">{r.business_date}</td>
                    <td className="px-3 py-2 text-text-sub break-words">{findNodeName(r.node_id)}</td>
                    <td className="px-3 py-2"><StatusBadge status={r.status} /></td>
                    <td className="px-3 py-2"><ReconciliationBadge status={r.reconciliation_status} /></td>
                    <td className="px-3 py-2 text-text-sub tabular-nums">{r.submitted_at ? r.submitted_at.slice(0, 16).replace('T', ' ') : '—'}</td>
                    <td className="px-3 py-2 text-right">
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/daily-reports/${r.id}`)}>詳細</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
